import { FileLinkWithMimeType } from 'src/constants/types';
import { CampaignConfigSchema, MessageTemplate, MessageType } from '../types';

/**
 * Which config template governs a message: one template governs the whole list;
 * otherwise the last template also governs messages appended beyond the list.
 */
export function getGoverningMessageTemplate(
  templates: CampaignConfigSchema['messageTemplates'] | undefined,
  index: number,
): MessageTemplate | undefined {
  if (!templates?.length) return undefined;
  if (templates.length === 1) return templates[0];
  return index < templates.length - 1 ? templates[index] : templates[templates.length - 1];
}

/**
 * Resolve a loaded message through its current config template. Locked
 * templates win first; a flexible template's current selection wins over the
 * stale type stored on the campaign or preset.
 *
 * `||`, not `??`: the declared type is `'sms' | 'mms'`, but a config off the admin
 * designer can carry `currentMessageType: ''`. An empty string is not nullish, so
 * `??` would make it the message's type and leave the SMS/MMS toggle with no
 * option matching its own value.
 */
export function resolveMessageType(
  template: MessageTemplate | undefined,
  storedType?: MessageType,
): MessageType {
  if (template?.messageType === 'mms-only') return 'mms';
  if (template?.messageType === 'sms-only') return 'sms';
  return template?.currentMessageType || storedType || 'sms';
}

/**
 * Rebuild `linksWithMimeType` from a legacy `links: string[]` array when the
 * richer field is absent, so loading a saved campaign — or a preset — keeps its
 * MMS attachment previews.
 */
export function normalizeLinksWithMimeType(message: {
  linksWithMimeType?: FileLinkWithMimeType[];
  links?: string[];
}): FileLinkWithMimeType[] {
  return message.linksWithMimeType || (message.links ? message.links.map(url => ({ url })) : []);
}
